import React from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { Flex, Spacer, Text } from "../../../styles/utility-components";
import { emptyStateIcons } from "./EmptyStateIcons";

type EmptyStateType =
  | "notifications"
  | "noResults"
  | "noDocuments"
  | "startingState"
  | "loadError"
  | "userRequiredContent"
  | "success"
  | "download";

interface EmptyStateProps {
  size?: "small" | "large";
  type?: EmptyStateType;
  heading?: string;
  description?: string | React.ReactNode;
  action?: React.ReactNode;
  width?: string;
  yAxisMargin?: string;
}

function EmptyState({
  size = "small",
  type = "noResults",
  heading,
  description,
  action,
  width = "300px",
  yAxisMargin,
}: EmptyStateProps) {
  const theme = useTheme();

  const isLarge = size === "large";

  return (
    <Wrapper width={width} yAxisMargin={yAxisMargin}>
      <Flex column justifyContent="center" alignItems="center">
        <UIcon
          svg={emptyStateIcons[type as keyof typeof emptyStateIcons]}
          size={isLarge ? 150 : 110}
        />
        <Flex column gap={4} justifyContent="center" alignItems="center">
          <Text h3={isLarge} fontWeight={isLarge ? "700" : "500"}>
            {heading}
          </Text>
          <TextWrapper description={!isLarge} center color={theme.colors.sysTextSecondary} as="div">
            {description}
          </TextWrapper>
          {action && (
            <>
              <Spacer vertical={isLarge ? 8 : 4} /> {/* +8px due to gap, so 16px and 12px */}
              {action}
            </>
          )}
        </Flex>
      </Flex>
    </Wrapper>
  );
}

export default EmptyState;

const Wrapper = styled.div<{ width: string; yAxisMargin?: string }>(({ width, yAxisMargin }) => ({
  width: width,
  ...(yAxisMargin && {
    marginTop: yAxisMargin,
    marginBottom: yAxisMargin,
  }),
}));

const TextWrapper = styled(Text)({
  maxWidth: "367px",
});
